import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
	FormioCustomComponent,
	ResourceNameService,
	FormioLoader,
} from 'dp-formio';
import { debounceTime, finalize, forkJoin, Subscription } from 'rxjs';
import { Utils } from 'formiojs';

declare var $: any;
@Component({
	selector: 'app-resource-dropdown-wrapper',
	templateUrl: './resource-dropdown-wrapper.component.html',
	// styleUrls: ['./resource-dropdown-wrapper.component.scss'],
	encapsulation: ViewEncapsulation.None,
	// providers: [FormioLoader]
})
export class ResourceDropdownWrapperComponent
	implements FormioCustomComponent<any>, OnChanges, OnDestroy
{
	dir: 'rtl' | 'ltr';
	@Input() required: boolean;
	@Input() value: any = {};
	@Output() valueChange = new EventEmitter<any>();
	@Input() disabled: boolean;
	@Input() viewOnly: boolean;
	@Input() data: any;
	@Input() customOptions: any;
	@Input() formioComponent: any;

	private subs: Subscription[] = [];
	set sub(s: Subscription) {
		this.subs.push(s);
	}

	resources: any[] = [];
	displayValue = '';
	isOpened = false;
	currentResourceLoading = null;
	randomId = new Date().getTime();

	filterControl = new FormControl();
	filter = '';
	get isSelectedResourceDisabledValueTranslation() {
		return this.customOptions?.resources?.find((q) => q.key == this.value?.resourceKey)
			?.disabledValueTranslation;
	}

	constructor(
		private router: Router,
		private resourceNameService: ResourceNameService,
		private translateService: TranslateService,
		private formioLoader: FormioLoader
	) {
		this.changeDirection(this.translateService.currentLang);
		this.sub = this.translateService.onLangChange.subscribe((data) => {
			this.changeDirection(data.lang);
		});
		this.registerCloseDroprown();

		this.sub = this.filterControl.valueChanges.pipe(debounceTime(350)).subscribe((value) => {
			this.filter = value;
			this.loadData();
		});
	}

	ngOnDestroy() {
		this.subs.forEach((s) => s.unsubscribe());
		this.subs = [];
	}

	ngOnChanges(changes: SimpleChanges) {
		if (this.customOptions) {
			if (this.customOptions.resources) {
				this.resources = [];
				this.customOptions.resources.forEach((res) => {
					const resource = { ...res };
					resource.key = this.formioComponent.interpolate(res.key, { data: this.data });
					resource.query = this.formioComponent.interpolate(res.query || '', { data: this.data });
					// resource.query = '';

					this.resources.push(resource);
				});
				this.loadData();
				if (this.value?.selectedItem) this.loadSelected();
			}
		}
	}

	onScrollDown(event: any, resourceIndex: number) {
		this.checkToLoadPage(resourceIndex);
	}

	selectItem(resourceKey: string, item: any) {
		this.notifyValue(resourceKey, item);

		const displayValue = this.customOptions.resources?.find(
			(q) => q.key == resourceKey
		)?.displayValue;
		this.displayValue = displayValue ? item.data[displayValue] : item.data.value;
		this.isOpened = false;
	}

	clearValue() {
		this.notifyValue();
		this.displayValue = null;
		this.isOpened = false;
	}

	notifyValue(resourceKey: string = null, item: any = null) {
		if (!resourceKey) {
			this.value = null;
			this.valueChange.emit(this.value);
		} else {
			this.value = {
				resourceKey,
				selectedItem: this.customOptions.selectedValueType == 'object' ? item : item._id,
			};
			this.valueChange.emit(this.value);
		}
		this.afterChangeValue();
	}

	private afterChangeValue() {
		if (this.customOptions && this.customOptions.afterChange)
			this.customOptions.afterChange.forEach((item) => {
				if (this.formioComponent.root.wizard) {
					const pages = this.formioComponent.root.getFastPages({
						all: true,
						withoutFirstLoad: true,
					});
					const components = Utils.searchComponents(pages, { key: item.key });
					components.forEach((c) => {
						if (item.checkConditions) {
							c.checkConditions();
						} else if (item.calculateValue) {
							c.calculateValue(this.data);
						} else {
							const value = Utils.evaluate(item.value, {
								data: this.data,
								instance: this.formioComponent,
							});
							c.setValue(value);
						}
					});
				} else {
					const nestedForms = Utils.searchComponents(this.formioComponent.root.components, {
						type: 'form',
						'component.display': 'wizard',
					});
					nestedForms.forEach((formComponent) => {
						formComponent.subFormReady.then((subForm) => {
							const pages = subForm.getFastPages({
								all: true,
								withoutFirstLoad: true,
							});
							const components = Utils.searchComponents(pages, {
								key: item.key,
							});
							components.forEach((c) => {
								if (item.checkConditions) {
									c.checkConditions();
								} else if (item.calculateValue) {
									c.calculateValue(this.data);
								} else {
									const value = Utils.evaluate(item.value, {
										data: this.data,
										instance: this.formioComponent,
									});
									c.setValue(value);
								}
							});
						});
					});

					const components = Utils.searchComponents(this.formioComponent.root.components, {
						key: item.key,
					});
					components.forEach((c) => {
						if (item.checkConditions) {
							c.checkConditions();
						} else if (item.calculateValue) {
							c.calculateValue(this.data);
						} else {
							const value = Utils.evaluate(item.value, {
								data: this.data,
								instance: this.formioComponent,
							});
							c.setValue(value);
						}
					});
				}
			});
	}

	private loadData() {
		// this.formioLoader.loading = true;
		this.sub = forkJoin(
			this.resources.map((res) =>
				this.resourceNameService.loadPage(
					res.key,
					(res.query || '') + this.resourceQueryFilter(res)
				)
			)
		)
			// .pipe(finalize(() => this.formioLoader.loading = false))
			.subscribe((results) => {
				this.resources.forEach((res, i) => {
					res.data = results[i].data;
					res.totalRecords = results[i].totalRecords;
				});
			});
	}

	private checkToLoadPage(resourceIndex: number) {
		if (this.resources[resourceIndex].totalRecords > this.resources[resourceIndex].data?.length) {
			this.currentResourceLoading = resourceIndex;
			const query =
				(this.resources[resourceIndex].query || '') +
				`limit=10&skip=${this.resources[resourceIndex].data?.length}` +
				this.resourceQueryFilter(this.resources[resourceIndex]);
			this.sub = this.resourceNameService
				.loadCustomByFilterdKey(this.resources[resourceIndex].key, query)
				.pipe(finalize(() => (this.currentResourceLoading = null)))
				.subscribe((data) => {
					this.resources[resourceIndex].data.push(...data);
				});
		}
	}

	private loadSelected() {
		this.sub = this.resourceNameService
			.loadCustomByKeyId(
				this.value.resourceKey,
				this.value.selectedItem._id || this.value.selectedItem
			)
			.subscribe((result) => {
				const displayValue = this.customOptions.resources?.find(
					(q) => q.key == this.value.resourceKey
				)?.displayValue;
				this.displayValue = displayValue ? result.data[displayValue] : result.data.value;
			});
	}

	private changeDirection(lang) {
		this.dir = null;
		setTimeout(() => {
			this.dir = lang === 'ar' ? 'rtl' : 'ltr';
		});
	}

	private registerCloseDroprown() {
		const instance = this;
		$(document).off('click');
		$(document).on('click', (event) => {
			var $trigger = $(`#resources-list-container-${this.randomId}`);
			if ($trigger !== event.target && !$trigger.has(event.target).length) {
				instance.isOpened = false;
			}
		});
	}

	private resourceQueryFilter(resource: any) {
		if (!this.filter) return ``;
		else if (resource?.filterkey) return `&data.${resource.filterkey}__regex=${this.filter}`;
		else
			return `&data.${this.translateService.currentLang == 'en' ? 'label' : 'arabicLabel'}__regex=${
				this.filter
			}`;
	}
}
